import { DOCUMENT, Injectable, inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { HttpActivityService } from './http-activity.service';

/** Opt a control out of the app-wide guard: `<button data-busy-skip …>`. */
const SKIP_ATTRIBUTE = 'data-busy-skip';
/** Marks the control the guard is currently holding (also the CSS hook — see `_controls.scss`). */
const BUSY_CLASS = 'is-busy-click';

/**
 * App-wide double-click guard, installed once from `app.config.ts` — **no per-component or per-template
 * wiring**. One capture-phase listener on the document: when a click on a button starts a **write**
 * (POST/PUT/PATCH/DELETE), that button is held until the write settles, and any further click on it is
 * swallowed before Angular's own `(click)` binding can run. So one impatient double-click can't approve
 * twice, delete twice, or submit twice — anywhere in the app, including buttons rendered later into
 * dialogs, menus and panels.
 *
 * Deliberately narrow, because it applies to every button in the app:
 * - **Writes only.** A button that merely re-reads (expander, period switch, filter) is released at once,
 *   so fast repeat clicking still works. Controls that want a spinner while *reading* opt in explicitly
 *   via `app-button` / `PortletButton` / grid actions, which run the same latch (`runBusyClick`).
 * - **Never touches `disabled`**, so it can't fight an existing `[disabled]` binding or Kendo's own button
 *   state. It marks `aria-busy` + a class; the blocking is done by swallowing the event.
 * - Opt out with `data-busy-skip` on the button (or an ancestor).
 */
@Injectable({ providedIn: 'root' })
export class BusyClickGuardService {
  private readonly document = inject(DOCUMENT);
  private readonly activity = inject(HttpActivityService);
  private installed = false;

  install(): void {
    if (this.installed) {
      return;
    }
    this.installed = true;
    this.document.addEventListener('click', this.onCapture, { capture: true });
  }

  private readonly onCapture = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const control = target?.closest<HTMLElement>('button, [role="button"]');
    if (!control || control.closest(`[${SKIP_ATTRIBUTE}]`)) {
      return;
    }

    if (control.classList.contains(BUSY_CLASS)) {
      // A write started by this control is still running — this click is the duplicate we exist to stop.
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const mark = this.activity.mark();
    // The handler runs after this capture listener, so give it a macrotask to fire its request before
    // asking whether anything is in flight. Nothing pending → released in the same breath.
    setTimeout(() => {
      if (!this.activity.hasPendingWritesAfter(mark)) {
        return;
      }
      control.classList.add(BUSY_CLASS);
      control.setAttribute('aria-busy', 'true');
      this.activity
        .settledAfter(mark, true)
        .pipe(
          finalize(() => {
            control.classList.remove(BUSY_CLASS);
            control.removeAttribute('aria-busy');
          })
        )
        .subscribe({ error: () => undefined });
    });
  };
}
